// CampaignContacts.tsx
import { Contact } from "@/lib/vibrato-api"
import { Modal, ModalBody, ModalFooter, ModalHeader } from "flowbite-react"
import React, { useContext, useEffect, useState } from "react"
import { Link, useNavigate, useParams } from "react-router-dom"
import { Loading } from "../Loading"
import ContactsTable from "./ContactsTable"
import { CampaignContext, VibratoClientContext } from "./contexts"
import CreateTaskTemplateCallModal from "./CreateTaskTemplateCallModal"

export default function CampaignContacts(): React.JSX.Element {
  const vibratoClient = useContext(VibratoClientContext)
  const campaign = useContext(CampaignContext)
  const navigate = useNavigate()
  const { uuid } = useParams()
  const [reloadCounter, setReloadCounter] = useState(0)
  const [selectedContacts, setSelectedContacts] = useState<any[]>([])
  const [showCreateTaskTemplateCallModal, setShowCreateTaskTemplateCallModal] = useState(false)
  const [selectedContactForTaskTemplate, setSelectedContactForTaskTemplate] = useState<Contact | null>(null)
  const [showDeleteContactsModal, setShowDeleteContactsModal] = useState(false)
  const [showReaddContactsModal, setShowReaddContactsModal] = useState(false)
  const [validationError, setValidationError] = useState<string>("")
  const [showInvalid, setShowInvalid] = useState(false)

  useEffect(() => {
    if (!uuid) {
      navigate("/campaigns")
    }
  }, [uuid, navigate])

  useEffect(() => {
    if (campaign.campaign) {
      setValidationError(campaign.campaign.validation_error)
    }
  }, [campaign])

  const reloadContacts = () => {
    setReloadCounter(reloadCounter + 1)
  }

  const openCreateTaskTemplateCallModal = (contact: Contact) => {
    setSelectedContactForTaskTemplate(contact)
    setShowCreateTaskTemplateCallModal(true)
  }

  if (!uuid) {
    return <Loading />
  }

  return (
    <div className="py-8 pr-8 h-full w-full flex flex-col gap-4">
      <CreateTaskTemplateCallModal
        show={showCreateTaskTemplateCallModal}
        onClose={() => setShowCreateTaskTemplateCallModal(false)}
        contact={selectedContactForTaskTemplate}
      />
      <div className="flex justify-between place-items-center gap-2">
        <h2 className="text-xl font-bold">Contacts</h2>
        <div className="flex flex-wrap gap-2">
          {!showInvalid && (
            <button
              className="bg-yellow-500 hover:bg-yellow-700 text-white font-semibold px-4 py-2 rounded-lg w-fit flex items-center gap-2"
              onClick={() => setShowInvalid(true)}
            >
              Show Invalid
            </button>
          )}
          {showInvalid && (
            <button
              className="bg-yellow-500 hover:bg-yellow-700 text-white font-semibold px-4 py-2 rounded-lg w-fit flex items-center gap-2"
              onClick={() => setShowInvalid(false)}
            >
              Show All
            </button>
          )}
          <button
            className="bg-red-500 hover:bg-red-700 text-white font-semibold px-4 py-2 rounded-lg w-fit"
            onClick={() => setShowDeleteContactsModal(true)}
          >
            Delete Selected
          </button>
          <button
            className="bg-blue-500 hover:bg-blue-700 text-white font-semibold px-4 py-2 rounded-lg w-fit"
            onClick={() => setShowReaddContactsModal(true)}
          >
            Call Selected Again
          </button>
          <Link to="/contacts">
            <button className="bg-green-400 hover:bg-green-700 text-white font-semibold px-4 py-2 rounded-lg w-fit">
              Add Contacts
            </button>
          </Link>
        </div>
      </div>
      {validationError && (
        <div
          className="p-4 mb-4 text-sm text-yellow-800 rounded-lg bg-yellow-50 dark:bg-gray-800 dark:text-yellow-300"
          role="alert"
        >
          {validationError.split("\n").map((error, index) => (
            <>
              {index === 0 ? (
                <div key={index}>
                  <span className="font-medium">Warning:</span> {error}
                </div>
              ) : (
                <div key={index}>{error}</div>
              )}
            </>
          ))}
        </div>
      )}
      <DeleteContactsModal
        show={showDeleteContactsModal}
        onClose={() => setShowDeleteContactsModal(false)}
        onConfirm={() => {
          reloadContacts()
        }}
        campaignUuid={uuid}
        contacts={selectedContacts}
      />
      <CallContactsAgainModal
        show={showReaddContactsModal}
        onClose={() => setShowReaddContactsModal(false)}
        onConfirm={() => {
          reloadContacts()
        }}
        campaignUuid={uuid}
        contacts={selectedContacts}
      />
      <ContactsTable
        vibratoClient={vibratoClient}
        reloadCounter={reloadCounter}
        setSelectedContacts={setSelectedContacts}
        openCreateTaskTemplateCallModal={openCreateTaskTemplateCallModal}
        campaignUuid={uuid}
        queryOptions={{ campaign_uuid: uuid }}
        showValidationErrors
        showInvalid={showInvalid}
      />
    </div>
  )
}

function DeleteContactsModal({
  show,
  onClose,
  onConfirm,
  campaignUuid,
  contacts
}: {
  show: boolean
  onClose: () => void
  onConfirm: () => void
  campaignUuid: string
  contacts: any
}) {
  const vibratoClient = useContext(VibratoClientContext)

  const removeContacts = async () => {
    const uuids = contacts.map((contact: any) => contact.uuid)
    await vibratoClient.removeContactFromCampaign(campaignUuid, uuids)
  }

  return (
    <Modal show={show} onClose={onClose} className="pt-safe">
      <ModalHeader className="border-b border-gray-200">
        <span className="text-red-600">Delete Contacts</span>
      </ModalHeader>
      <ModalBody>
        <p>Are you sure you want to delete these contacts from the campaign?</p>
        <ul>
          {contacts.map((contact: any, index: number) => (
            <li key={index}>{contact.name}</li>
          ))}
        </ul>
      </ModalBody>
      <ModalFooter>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={onClose}
            className="mt-2 bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded w-fit"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={async () => {
              await removeContacts()
              onConfirm()
              onClose()
            }}
            className="mt-2 bg-red-500 hover:bg-red-700 text-white font-bold py-2 px-4 rounded w-fit"
          >
            Delete
          </button>
        </div>
      </ModalFooter>
    </Modal>
  )
}
function CallContactsAgainModal({
  show,
  onClose,
  onConfirm,
  campaignUuid,
  contacts
}: {
  show: boolean
  onClose: () => void
  onConfirm: () => void
  campaignUuid: string
  contacts: any
}) {
  const vibratoClient = useContext(VibratoClientContext)
  const [error, setError] = useState<string>("")

  const readdContacts = async () => {
    const uuids = contacts.map((contact: any) => contact.uuid)
    try {
      await vibratoClient.batchAddContactsToCampaign(campaignUuid, uuids, true)
      onConfirm()
    } catch (err) {
      setError("Error re-adding contacts to campaign. Please try again.")
    }
  }

  return (
    <Modal show={show} onClose={onClose} className="pt-safe">
      <ModalHeader className="border-b border-gray-200">
        <span className="text-blue-600">Call {contacts.length} Contact(s) Again</span>
      </ModalHeader>
      <ModalBody>
        <p>
          Are you sure you want to call these contacts again? Only contacts that haven't been called yet will be called.
        </p>
        <ul>
          {contacts.map((contact: any, index: number) => (
            <li key={index}>{contact.name}</li>
          ))}
        </ul>
        {error && <p className="text-red-500">{error}</p>}
      </ModalBody>
      <ModalFooter>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={onClose}
            className="mt-2 bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded w-fit"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={async () => {
              await readdContacts()
              onClose()
            }}
            className="mt-2 bg-green-500 hover:bg-green-700 text-white font-bold py-2 px-4 rounded w-fit"
          >
            Confirm
          </button>
        </div>
      </ModalFooter>
    </Modal>
  )
}
